import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProject, getPlots, createPlot, updatePlot, deletePlot } from '../utils/api';
import { Plus, Pencil, Trash2, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'booked', label: 'Booked' },
  { value: 'hold', label: 'On Hold (24h)' },
  { value: 'nfs', label: 'Not For Sale' },
];

const EMPTY_PLOT = { plot_no: '', facing: '', size: '', status: 'available', booked_by: '', notes: '', price: '' };

function PlotModal({ plot, projectId, onClose }) {
  const qc = useQueryClient();
  const isEdit = !!plot?.id;
  const [form, setForm] = useState(plot ? {
    plot_no: plot.plot_no || '',
    facing: plot.facing || '',
    size: plot.size || '',
    status: plot.status || 'available',
    booked_by: plot.booked_by || '',
    notes: plot.notes || '',
    price: plot.price || '',
  } : EMPTY_PLOT);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: isEdit
      ? (data) => updatePlot(plot.id, data)
      : (data) => createPlot({ ...data, project_id: projectId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plots', projectId] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      toast.success(isEdit ? 'Plot updated' : 'Plot added');
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Error'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.plot_no) return toast.error('Plot number required');
    mutation.mutate({
      ...form,
      price: form.price ? parseFloat(form.price) : null,
      booked_by: form.booked_by || null,
      notes: form.notes || null,
    });
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-head">
          <h3>{isEdit ? `Edit Plot ${plot.plot_no}` : 'Add New Plot'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Plot No. *</label>
                <input className="form-input" value={form.plot_no} onChange={e => set('plot_no', e.target.value)} placeholder="e.g. Plot-10 or Shop-3" required />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                  {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Facing</label>
                <select className="form-select" value={form.facing} onChange={e => set('facing', e.target.value)}>
                  <option value="">Select facing</option>
                  {['North', 'South', 'East', 'West', 'North-East', 'North-West', 'South-East', 'South-West', 'South-North', 'North-West', '—'].map(f => (
                    <option key={f}>{f}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Size</label>
                <input className="form-input" value={form.size} onChange={e => set('size', e.target.value)} placeholder="e.g. 100'-00&quot;" />
              </div>
            </div>

            {(form.status === 'booked' || form.status === 'hold') && (
              <div className="form-group">
                <label className="form-label">
                  {form.status === 'booked' ? 'Customer Name' : 'Hold By (Customer)'}
                </label>
                <input className="form-input" value={form.booked_by} onChange={e => set('booked_by', e.target.value)}
                  placeholder="Enter customer name" />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Price (₹)</label>
              <input className="form-input" type="number" value={form.price} onChange={e => set('price', e.target.value)} placeholder="Optional" />
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any additional notes..." style={{ minHeight: 60 }} />
            </div>

            {form.status === 'hold' && (
              <div style={{ background: 'var(--amber-light)', border: '1px solid #EF9F27', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: '#b36a00' }}>
                ⚠️ Hold will automatically expire after 24 hours.
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-green btn-sm" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Plot'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const STATUS_LABELS = { available: 'Available', booked: 'Booked', hold: 'On Hold', nfs: 'Not For Sale' };

export default function AdminPlots() {
  const { projectId } = useParams();
  const qc = useQueryClient();
  const [modal, setModal] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const { data: project, isLoading: projLoading } = useQuery({
    queryKey: ['project', projectId], queryFn: () => getProject(projectId)
  });

  const { data: plots = [], isLoading: plotsLoading } = useQuery({
    queryKey: ['plots', projectId], queryFn: () => getPlots({ project_id: projectId })
  });

  const delMutation = useMutation({
    mutationFn: deletePlot,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plots', projectId] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      toast.success('Plot deleted');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Error'),
  });

  const handleDelete = (plot) => {
    if (!window.confirm(`Delete Plot ${plot.plot_no}?`)) return;
    delMutation.mutate(plot.id);
  };

  // Quick status change
  const quickStatus = async (plot, newStatus) => {
    try {
      await updatePlot(plot.id, { status: newStatus, booked_by: newStatus === 'available' ? null : plot.booked_by });
      qc.invalidateQueries({ queryKey: ['plots', projectId] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      toast.success(`Plot ${plot.plot_no} marked as ${STATUS_LABELS[newStatus]}`);
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const filtered = statusFilter === 'all' ? plots : plots.filter(p => p.status === statusFilter);

  if (projLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  return (
    <div style={{ padding: 28 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/admin/projects" className="btn btn-ghost btn-sm btn-icon"><ArrowLeft size={16} /></Link>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700 }}>{project?.name}</h1>
            <p style={{ color: 'var(--text3)', fontSize: 13 }}>{project?.type} · {plots.length} total plots</p>
          </div>
        </div>
        <button className="btn btn-green" onClick={() => setModal('new')}>
          <Plus size={16} /> Add Plot
        </button>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {['available', 'booked', 'hold', 'nfs'].map(s => (
          <div key={s} className="stat-card" style={{ cursor: 'pointer', transition: 'all 0.15s', border: statusFilter === s ? '2px solid var(--green)' : undefined }}
            onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}>
            <div className="stat-val" style={{
              color: s === 'available' ? 'var(--green)' : s === 'booked' ? 'var(--red)' : s === 'hold' ? 'var(--amber)' : 'var(--gray)',
              fontSize: 24
            }}>
              {plots.filter(p => p.status === s).length}
            </div>
            <div className="stat-lbl">{STATUS_LABELS[s]}</div>
          </div>
        ))}
      </div>

      {/* Filter chips */}
      <div className="filter-bar" style={{ borderRadius: '12px 12px 0 0', border: '1px solid var(--border)', borderBottom: 'none' }}>
        <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 500 }}>Filter:</span>
        <button className={`fchip ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => setStatusFilter('all')}>All ({plots.length})</button>
        {['available', 'booked', 'hold', 'nfs'].map(s => (
          <button key={s} className={`fchip ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
            {STATUS_LABELS[s]} ({plots.filter(p => p.status === s).length})
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="card" style={{ borderRadius: '0 0 12px 12px' }}>
        {plotsLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><p>No plots match this filter.</p></div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Plot No.</th>
                  <th>Facing</th>
                  <th>Size</th>
                  <th>Status</th>
                  <th>Customer</th>
                  <th>Quick Actions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((plot, i) => (
                  <tr key={plot.id}>
                    <td className="mono">{i + 1}</td>
                    <td style={{ fontWeight: 700 }}>{plot.plot_no}</td>
                    <td style={{ fontSize: 12 }}>{plot.facing || '—'}</td>
                    <td className="mono">{plot.size || '—'}</td>
                    <td><span className={`badge badge-${plot.status}`}>{STATUS_LABELS[plot.status]}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{plot.booked_by || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {plot.status !== 'available' && (
                          <button className="btn btn-sm" style={{ background: 'var(--blue-light)', color: '#185FA5', border: 'none', fontSize: 11 }}
                            onClick={() => quickStatus(plot, 'available')}>✓ Available</button>
                        )}
                        {plot.status !== 'booked' && plot.status !== 'nfs' && (
                          <button className="btn btn-sm" style={{ background: 'var(--green-light)', color: 'var(--green-dark)', border: 'none', fontSize: 11 }}
                            onClick={() => setModal({ ...plot, _quickBook: true })}>Book</button>
                        )}
                        {plot.status !== 'hold' && plot.status !== 'nfs' && plot.status !== 'booked' && (
                          <button className="btn btn-sm" style={{ background: 'var(--amber-light)', color: '#b36a00', border: 'none', fontSize: 11 }}
                            onClick={() => quickStatus(plot, 'hold')}>Hold 24h</button>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-outline btn-sm btn-icon" onClick={() => setModal(plot)} title="Edit">
                          <Pencil size={13} />
                        </button>
                        <button className="btn btn-sm btn-icon" onClick={() => handleDelete(plot)} title="Delete"
                          style={{ background: 'var(--red-light)', color: 'var(--red)', border: 'none' }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <PlotModal
          plot={modal === 'new' ? null : modal}
          projectId={projectId}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
